import { runAssetsCurrentlyAssignedReport } from '@/reports/runners/assetsCurrentlyAssignedRunner';
import { runAllocationSummaryReport } from '@/reports/runners/allocationSummaryRunner';
import { runOverdueAssetsReport } from '@/reports/runners/overdueAssetsRunner';
import { runTransactionCompletionReport } from '@/reports/runners/transactionCompletionRunner';
import { runDxReport } from '@/reports/runners/dxReportRunner';
import { runAllIeReport } from '@/reports/runners/allIeRunner';
import { runIeItemsNotOnPhrReport } from '@/reports/runners/ieItemsNotOnPhrRunner';
import { runClearedIndividualsReport } from '@/reports/runners/clearedIndividualsRunner';
import { runCustomerSurveyReport } from '@/reports/runners/customerSurveyRunner';
import { runAssetAcquisitionOrderReport } from '@/reports/runners/assetAcquisitionOrderRunner';
import { runOutstandingPosReport } from '@/reports/runners/outstandingPosRunner';
import { runCostOfBusinessReport } from '@/reports/runners/costOfBusinessRunner';
import { runInventoryTrendsReport } from '@/reports/runners/inventoryTrendsRunner';
import {
  INVENTORY_AWARENESS_CATEGORY_OPTIONS,
  runInventoryAwarenessReport,
} from '@/reports/runners/inventoryAwarenessRunner';
import { runSuggestOnHandInventoryReport } from '@/reports/runners/suggestOnHandInventoryRunner';
import { runPrimaryRestockReport } from '@/reports/runners/primaryRestockRunner';
import { runAssetsApproachingExpirationReport } from '@/reports/runners/assetsApproachingExpirationRunner';
import { runExpiredAssetsReport } from '@/reports/runners/expiredAssetsRunner';
import { runChangeDocumentReport } from '@/reports/runners/changeDocumentRunner';
import { runWarehouseShelfMapReport } from '@/reports/runners/warehouseShelfMapRunner';
import { runTotalClothingSizeByWorkforceCategoryReport } from '@/reports/runners/totalClothingSizeByWorkforceCategoryRunner';
import { runPropertyBookListingReport } from '@/reports/runners/propertyBookListingRunner';
import { runInventoryMovementReport } from '@/reports/runners/inventoryMovementRunner';
import { DYNAMIC_OPTIONS, type DynamicOptionType } from '@/reports/constants';
import type { PersonLabels } from '@/hooks/usePersonLabels';

type BaseReportParameterSchema = {
  name: string;
  label: string;
  required?: boolean;
};

type TextReportParameterSchema = BaseReportParameterSchema & {
  type: 'string' | 'date';
  defaultValue?: string;
};

type BooleanReportParameterSchema = BaseReportParameterSchema & {
  type: 'boolean';
  defaultValue?: boolean;
};

export type SelectOptionItem = { value: string; label: string };

type SelectReportParameterSchema = BaseReportParameterSchema &
  (
    | {
        type: 'select';
        options: (string | SelectOptionItem)[];
        dynamicOptions?: never;
        defaultValue?: string;
      }
    | {
        type: 'select';
        dynamicOptions: DynamicOptionType;
        options?: never;
        defaultValue?: string;
      }
  );

type MultiSelectReportParameterSchema = BaseReportParameterSchema &
  (
    | {
        type: 'multiselect';
        options: string[];
        dynamicOptions?: never;
        defaultValue?: string[];
      }
    | {
        type: 'multiselect';
        dynamicOptions: DynamicOptionType;
        options?: never;
        defaultValue?: string[];
      }
  );

type PersonReportParameterSchema = BaseReportParameterSchema & {
  type: 'person';
};

type MultiPersonReportParameterSchema = BaseReportParameterSchema & {
  type: 'multiperson';
};

export type ReportParameterSchema =
  | TextReportParameterSchema
  | BooleanReportParameterSchema
  | SelectReportParameterSchema
  | MultiSelectReportParameterSchema
  | PersonReportParameterSchema
  | MultiPersonReportParameterSchema;

export interface ReportDefinition {
  id: string;
  name: string;
  description: string;
  parameters: ReportParameterSchema[];
  run: (params: Record<string, unknown>) => Promise<void> | void;
}

const DEFAULT_PERSON: PersonLabels = {
  label: 'Individual',
  labelPlural: 'Individuals',
  lower: 'individual',
  lowerPlural: 'individuals',
  article: 'an',
  individualIdLabel: 'Individual ID',
  individualIdPrefix: 'INDV-',
};

/** Build the reports registry with the given person labels. */
export function buildReportsRegistry(
  p: PersonLabels = DEFAULT_PERSON,
  ieKitLabel = 'IE Kit',
  ieKitLabelPlural = 'IE Kits',
  primaryLocationLabel = 'Primary',
  secondaryLocationLabel = 'Secondary',
  excludeReportLabel = 'Exclude Restricted',
): ReportDefinition[] {
  return [
    {
      id: 'allocation-summary',
      name: 'Allocation Summary',
      description: `Print summary of assigned assets for a specific ${p.label}.`,
      parameters: [
        {
          name: 'person',
          label: p.label,
          type: 'person',
          required: true,
        },
      ],
      run: runAllocationSummaryReport,
    },
    {
      id: 'transaction-completion',
      name: 'Transaction Completion Report',
      description:
        'Shows transactions within a date range, grouped by user, with elapsed time and an overall average completion time.',
      parameters: [
        {
          name: 'fromDate',
          label: 'From Date',
          type: 'date',
        },
        {
          name: 'toDate',
          label: 'To Date',
          type: 'date',
        },
        {
          name: 'userEmail',
          label: 'User (email contains)',
          type: 'string',
        },
      ],
      run: runTransactionCompletionReport,
    },
    {
      id: 'dx-report',
      name: 'DX Report',
      description: `Shows DX (exchange) transactions within a date range, including LIN/NSN and associated ${p.lower} when available.`,
      parameters: [
        {
          name: 'fromDate',
          label: 'From Date',
          type: 'date',
        },
        {
          name: 'toDate',
          label: 'To Date',
          type: 'date',
        },
      ],
      run: runDxReport,
    },
    {
      id: 'all-ie',
      name: `All ${ieKitLabelPlural}`,
      description: `Lists all ${ieKitLabelPlural.toLowerCase()} and their authorized items across the system, grouped by ${ieKitLabel.toLowerCase()} name.`,
      parameters: [
        {
          name: 'ieFilter',
          label: `${ieKitLabelPlural} (Optional)`,
          type: 'multiselect',
          dynamicOptions: DYNAMIC_OPTIONS.IE_KITS,
        },
      ],
      run: runAllIeReport,
    },
    {
      id: 'ie-items-not-on-sm-phr',
      name: `${ieKitLabel} Items Not on PHR`,
      description: `Shows ${ieKitLabel}-authorized items that have not yet been issued to the selected ${p.lower} (under-authorized items).`,
      parameters: [
        {
          name: 'person',
          label: `${p.label} (Optional)`,
          type: 'person',
        },
        {
          name: 'linSearch',
          label: 'LIN search (Optional) (comma-separated)',
          type: 'string',
        },
        {
          name: 'ieFilter',
          label: `${ieKitLabel} (Optional)`,
          type: 'multiselect',
          dynamicOptions: DYNAMIC_OPTIONS.IE_KITS,
        },
      ],
      run: runIeItemsNotOnPhrReport,
    },
    {
      id: 'cleared-service-members',
      name: `Cleared ${p.labelPlural} Report`,
      description: `Shows transactions within a date range that contribute to clearing ${p.lowerPlural}, including transaction type and associated user.`,
      parameters: [
        {
          name: 'fromDate',
          label: 'From Date',
          type: 'date',
        },
        {
          name: 'toDate',
          label: 'To Date',
          type: 'date',
        },
        {
          name: 'person',
          label: `${p.label} (Optional)`,
          type: 'person',
        },
        {
          name: 'memberStatus',
          label: `${p.label} Status`,
          type: 'select',
          options: ['cleared', 'active', 'all'],
          defaultValue: 'cleared',
        },
      ],
      run: runClearedIndividualsReport,
    },
    {
      id: 'customer-survey',
      name: 'Customer Survey Report',
      description:
        'Lists customer survey responses within a date range, filtered optionally by user email.',
      parameters: [
        {
          name: 'fromDate',
          label: 'From Date',
          type: 'date',
        },
        {
          name: 'toDate',
          label: 'To Date',
          type: 'date',
        },
        {
          name: 'userEmail',
          label: 'User (email contains)',
          type: 'string',
        },
      ],
      run: runCustomerSurveyReport,
    },
    {
      id: 'asset-acquisition-order-form',
      name: 'Asset Acquisition Order Form',
      description:
        'Printable order form for purchase requisitions, with optional filtering by order number, log form number, and date range.',
      parameters: [
        {
          name: 'orderNumber',
          label: 'Order Number',
          type: 'string',
          required: false,
        },
        {
          name: 'logFormNumber',
          label: 'Log Form Number',
          type: 'string',
          required: false,
        },
        {
          name: 'startDate',
          label: 'Start Date',
          type: 'date',
          required: false,
        },
        {
          name: 'endDate',
          label: 'End Date',
          type: 'date',
          required: false,
        },
      ],
      run: runAssetAcquisitionOrderReport,
    },
    {
      id: 'outstanding-pos',
      name: 'Outstanding POs',
      description:
        'Lists open purchase orders with line items that still have outstanding quantities.',
      parameters: [],
      run: runOutstandingPosReport,
    },
    {
      id: 'cost-of-business',
      name: 'Cost of Business Report',
      description:
        'Summarizes transaction assets over a date range with optional LIN/member/org/billet/issue-status filters and line costs.',
      parameters: [
        {
          name: 'fromDate',
          label: 'From Date',
          type: 'date',
        },
        {
          name: 'toDate',
          label: 'To Date',
          type: 'date',
        },
        {
          name: 'linFilter',
          label: 'LIN(s) (Optional; comma-separated)',
          type: 'string',
        },
        {
          name: 'organization',
          label: 'Organization (Optional)',
          type: 'string',
        },
        {
          name: 'individual',
          label: `${p.label} (Optional)`,
          type: 'person',
        },
        {
          name: 'billet',
          label: 'Billet (Optional)',
          type: 'string',
        },
        {
          name: 'issueStatus',
          label: 'Issue Status (Optional)',
          type: 'select',
          options: [
            { value: 'on_ie', label: `On ${ieKitLabel}` },
            { value: 'not_on_ie', label: `Not on ${ieKitLabel}` },
          ],
        },
        {
          name: 'transactionType',
          label: 'Transaction Type (Optional)',
          type: 'select',
          options: ['ISSUE_OUT', 'TURN_IN', 'EXCHANGE', 'DISPOSAL'],
        },
      ],
      run: runCostOfBusinessReport,
    },
    {
      id: 'inventory-trends',
      name: 'Inventory Trends Report',
      description:
        'Ranks items by issue activity over a period and shows total, average monthly, last 30 days, and a simple trend percentage.',
      parameters: [
        {
          name: 'fromDate',
          label: 'From Date',
          type: 'date',
        },
        {
          name: 'toDate',
          label: 'To Date',
          type: 'date',
        },
      ],
      run: runInventoryTrendsReport,
    },
    {
      id: 'inventory-awareness',
      name: 'Inventory Awareness Report',
      description:
        'Groups inventory items by awareness category (out of stock, normal range, etc.); currently uses stock counts, thresholds will be wired once available.',
      parameters: [
        {
          name: 'assetCategory',
          label: 'Asset Category (Optional)',
          type: 'select',
          options: [...INVENTORY_AWARENESS_CATEGORY_OPTIONS],
        },
      ],
      run: runInventoryAwarenessReport,
    },
    {
      id: 'suggest-on-hand-inventory',
      name: 'Suggest On Hand Inventory',
      description:
        'Summarizes quantity issued per item by month as a starting point for suggested on-hand levels (based on recent transaction history).',
      parameters: [
        {
          name: 'startDate',
          label: 'Start Date',
          type: 'date',
          required: true,
        },
        {
          name: 'endDate',
          label: 'End Date',
          type: 'date',
          required: true,
        },
        {
          name: 'linFilter',
          label: 'LIN (Optional; comma-separated)',
          type: 'string',
        },
        {
          name: 'ieFilter',
          label: `${ieKitLabelPlural} (Optional)`,
          type: 'multiselect',
          dynamicOptions: DYNAMIC_OPTIONS.IE_KITS,
        },
      ],
      run: runSuggestOnHandInventoryReport,
    },
    {
      id: 'primary-restock',
      name: `${primaryLocationLabel} Restock Report`,
      description: `Shows ${primaryLocationLabel.toLowerCase()} items below their minimum threshold with suggested restock quantity and available ${secondaryLocationLabel.toLowerCase()} quantities.`,
      parameters: [],
      run: runPrimaryRestockReport,
    },
    {
      id: 'assets-approaching-expiration',
      name: 'Assets Approaching Expiration Date',
      description:
        'Lists assets with an expiration date within the selected window (defaults to the next 60 days).',
      parameters: [
        {
          name: 'expiringByDate',
          label: 'Expiring by date',
          type: 'date',
          defaultValue: new Date(Date.now() + 60 * 24 * 3600 * 1000).toISOString().slice(0, 10),
        },
        {
          name: 'assetScope',
          label: 'Issued Assets / Inventory Assets / Both',
          type: 'select',
          options: ['issued', 'inventory', 'both'],
          defaultValue: 'both',
        },
      ],
      run: runAssetsApproachingExpirationReport,
    },
    {
      id: 'expired-assets',
      name: 'Expired Assets Report',
      description:
        'Lists assets whose expiration date is in the past; quantity and assignment are approximated until a richer endpoint is available.',
      parameters: [
        {
          name: 'expiringByDate',
          label: 'Expiring by date',
          type: 'date',
        },
      ],
      run: runExpiredAssetsReport,
    },
    {
      id: 'assets-currently-assigned',
      name: 'Assets Currently Assigned',
      description: 'Lists currently assigned assets from the asset inventory summary view.',
      parameters: [
        {
          name: 'persons',
          label: `${p.labelPlural} (Optional)`,
          type: 'multiperson',
        },
        {
          name: 'assetType',
          label: 'Asset Type (PHR / THR / Both)',
          type: 'select',
          options: ['PHR', 'THR', 'both'],
          defaultValue: 'both',
        },
        {
          name: 'assetStatus',
          label: 'Asset Status (Active / Retired / Both)',
          type: 'select',
          options: ['active', 'retired', 'both'],
          defaultValue: 'active',
        },
        {
          name: 'receiptStartDate',
          label: 'Receipt Start Date',
          type: 'date',
          required: false,
        },
        {
          name: 'receiptEndDate',
          label: 'Receipt End Date',
          type: 'date',
          required: false,
        },
        {
          name: 'excludePnp',
          label: 'Exclude PnP',
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'excludeSoc',
          label: excludeReportLabel,
          type: 'boolean',
          defaultValue: false,
        },
        {
          name: 'viewPictures',
          label: 'View Pictures',
          type: 'boolean',
          defaultValue: true,
        },
      ],
      run: runAssetsCurrentlyAssignedReport,
    },
    {
      id: 'overdue-assets',
      name: 'Overdue Assets',
      description:
        'Lists assets with an overdue status (THR only). PHR data is not currently available.',
      parameters: [
        {
          name: 'fromDate',
          label: 'From Date',
          type: 'date',
          required: false,
        },
        {
          name: 'toDate',
          label: 'To Date',
          type: 'date',
          required: false,
        },
        {
          name: 'person',
          label: `${p.label} (Optional)`,
          type: 'person',
        },
        {
          name: 'ieFilter',
          label: 'LIN (comma-separated)',
          type: 'string',
        },
        {
          name: 'qtyAuthLowerThanIssued',
          label: 'Qty Authorized lower than Qty Issued',
          type: 'boolean',
          defaultValue: false,
        },
      ],
      run: runOverdueAssetsReport,
    },
    {
      id: 'change-document',
      name: 'Change Document',
      description: `Printable Change Document for a ${p.lower} within a date range (Issue/Turn In/DX summary).`,
      parameters: [
        {
          name: 'person',
          label: p.label,
          type: 'person',
          required: false,
        },
        {
          name: 'startDate',
          label: 'Start Date',
          type: 'date',
          required: true,
        },
        {
          name: 'endDate',
          label: 'End Date',
          type: 'date',
          required: true,
        },
      ],
      run: runChangeDocumentReport,
    },
    {
      id: 'warehouse-shelf-map',
      name: 'Warehouse Shelf Map',
      description:
        'Prints the shelf-map layout for a warehouse showing aisles, shelf rows, column slots, and on-hand inventory. Supports aisle/row filtering and multi-location selection.',
      parameters: [
        {
          name: 'layoutId',
          label: 'Layout',
          type: 'select',
          dynamicOptions: DYNAMIC_OPTIONS.SHELF_MAP_LAYOUTS,
          required: true,
        },
        {
          name: 'aisleFilter',
          label: 'Aisle Filter (optional)',
          type: 'string',
          required: false,
        },
        {
          name: 'rowFilter',
          label: 'Shelf Row Number (optional)',
          type: 'string',
          required: false,
        },
        {
          name: 'locationIds',
          label: 'Locations (optional — select one or more to filter by location)',
          type: 'multiselect',
          dynamicOptions: DYNAMIC_OPTIONS.SHELF_MAP_LOCATIONS,
          required: false,
        },
      ],
      run: runWarehouseShelfMapReport,
    },
    {
      id: 'total-clothing-size-by-workforce-category',
      name: 'Total Clothing Size by Workforce Category',
      description:
        'Shows sized clothing counts per Parent NSN and size, using service member sizing as the source of truth, scoped to a Workforce Category and optional Section.',
      parameters: [
        {
          name: 'workforceCategory',
          label: 'Workforce Category',
          type: 'select',
          dynamicOptions: DYNAMIC_OPTIONS.WORKFORCE_CATEGORIES,
          required: true,
        },
        {
          name: 'section',
          label: 'Section (Optional)',
          type: 'select',
          dynamicOptions: DYNAMIC_OPTIONS.SECTIONS_BY_WORKFORCE_CATEGORY,
          required: false,
        },
        {
          name: 'parentNsnIds',
          label: 'Parent NSN (Optional)',
          type: 'multiselect',
          dynamicOptions: DYNAMIC_OPTIONS.PARENT_NSN_BY_WORKFORCE_CATEGORY,
          required: false,
        },
      ],
      run: runTotalClothingSizeByWorkforceCategoryReport,
    },
    {
      id: 'property-book-listing',
      name: 'Property Book Listing',
      description:
        'Property book items sorted by Location, showing on-hand quantities, unit and total price, and ASL Value aggregated per NSN.',
      parameters: [
        {
          name: 'locationId',
          label: 'Location',
          type: 'select',
          dynamicOptions: DYNAMIC_OPTIONS.LOCATIONS,
          defaultValue: '',
        },
        {
          name: 'lin',
          label: 'LIN (Optional)',
          type: 'string',
          required: false,
        },
      ],
      run: runPropertyBookListingReport,
    },
    {
      id: 'inventory-movement-report',
      name: 'Inventory Movement Report',
      description:
        'Compares requisition (purchase) quantity/cost against disposal quantity/cost per NSN over a date range.',
      parameters: [
        { name: 'fromDate', label: 'From Date', type: 'date', required: true },
        { name: 'toDate', label: 'To Date', type: 'date', required: true },
      ],
      run: runInventoryMovementReport,
    },
  ];
}

/** Default registry (product-standard labels) for consumers that don't have person labels. */
export const reportsRegistry: ReportDefinition[] = buildReportsRegistry();
